import * as React from "react";

import { getRTL } from "@fluentui/react/lib/Utilities";

import {
  AppPaddleButton,
  appPaddleWidth,
  PaddleOrientation,
} from "../AppPaddleButton";
import { AppOverflowPaddleControlProps } from "./AppOverflowPaddleControl.Props";

export interface AppOverflowPaddleControlState {
  paddleOrientation: PaddleOrientation;
}

export class AppOverflowPaddleControl extends React.PureComponent<
  AppOverflowPaddleControlProps,
  AppOverflowPaddleControlState
> {
  private container = React.createRef<HTMLDivElement>();

  // Number of pixels we will allow the calculations to be off by.
  private paddlePixelThreshold = 1;
  private isRTL = false;
  private leftPaddleLabel = "";
  private rightPaddleLabel = "";

  constructor(props: AppOverflowPaddleControlProps) {
    super(props);
    this.isRTL = getRTL();
    if (this.isRTL) {
      this.leftPaddleLabel = this.props.rightRibbonPaddleLabel;
      this.rightPaddleLabel = this.props.leftRibbonPaddleLabel;
    } else {
      this.leftPaddleLabel = this.props.leftRibbonPaddleLabel;
      this.rightPaddleLabel = this.props.rightRibbonPaddleLabel;
    }

    this.state = {
      paddleOrientation: PaddleOrientation.Hidden,
    };
  }

  public render() {
    const { downChevronIcon } = this.props;

    return (
      <div ref={this.container} id="paddleContainer">
        {this.props.children}
        {(this.state.paddleOrientation === PaddleOrientation.Right ||
          this.state.paddleOrientation === PaddleOrientation.LeftRight) && (
          <AppPaddleButton
            id={"RightPaddle"}
            icon={downChevronIcon}
            orientation={PaddleOrientation.Right}
            onExecute={this.onPaddleButtonExecute}
            label={this.rightPaddleLabel}
            disableModeTogglePadding={this.props.disableModeTogglePadding}
          />
        )}
        {(this.state.paddleOrientation === PaddleOrientation.Left ||
          this.state.paddleOrientation === PaddleOrientation.LeftRight) && (
          <AppPaddleButton
            id={"LeftPaddle"}
            icon={downChevronIcon}
            orientation={PaddleOrientation.Left}
            onExecute={this.onPaddleButtonExecute}
            label={this.leftPaddleLabel}
            disableModeTogglePadding={this.props.disableModeTogglePadding}
          />
        )}
      </div>
    );
  }

  public componentDidUpdate(
    _prevProps: AppOverflowPaddleControlProps,
    prevState: AppOverflowPaddleControlState
  ) {
    if (
      prevState.paddleOrientation === PaddleOrientation.Hidden &&
      this.state.paddleOrientation !== PaddleOrientation.Hidden
    ) {
      this.props.onPaddlesShown?.();
    }
  }

  public isPaddlesVisible = () => {
    return this.state.paddleOrientation !== PaddleOrientation.Hidden;
  };

  public isEndPaddleVisible = () => {
    return (
      this.state.paddleOrientation === PaddleOrientation.Right ||
      this.state.paddleOrientation === PaddleOrientation.LeftRight
    );
  };

  public hideRibbonPaddles = () => {
    this.setState({ paddleOrientation: PaddleOrientation.Hidden });
  };

  /**
   * Function that handles determining if we should add paddles to the ribbon
   */
  public handleChildOverflow = () => {
    if (this.container.current && this.container.current.children) {
      const overflowElement = this.container.current.children[0];
      if (overflowElement.scrollWidth > overflowElement.clientWidth) {
        if (this.isRTL) {
          this.handleChildOverflowRightToLeft(overflowElement);
        } else {
          this.handleChildOverflowLeftToRight(overflowElement);
        }
      } else {
        this.setState({ paddleOrientation: PaddleOrientation.Hidden });
      }
    }
  };

  private handleChildOverflowRightToLeft = (overflowElement: Element) => {
    // Ribbon paddles will be add if there is content (scrollWidth) outside of what can be seen (clientWidth).
    // If we have the scrollWidth-scrollLeft, which is > 0 in RTL if any content is off screen
    // is less than the clientWidth than we just need to add a paddle to the left.
    // If we have some amount of content off the screen to the left, but the width is
    // still greater than the clientWidth we need to add a paddle on both sides.  However, if the scrollLeft
    // is less than the pixels of the paddle, we will scroll over the additional amount to avoid
    // a control being slightly cut off.
    // Else we add it to just the left
    if (
      overflowElement.scrollWidth - overflowElement.scrollLeft <=
      overflowElement.clientWidth + this.paddlePixelThreshold
    ) {
      this.setState({ paddleOrientation: PaddleOrientation.Right });
    } else if (
      overflowElement.scrollWidth - overflowElement.scrollLeft >
      overflowElement.clientWidth
    ) {
      // If the content that remains off screener is less than the size of the paddle, scroll
      // it into view.
      if (overflowElement.scrollLeft < appPaddleWidth) {
        overflowElement.scrollLeft = 0;
        this.setState({ paddleOrientation: PaddleOrientation.Left });
        return;
      }
      this.setState({ paddleOrientation: PaddleOrientation.LeftRight });
    } else {
      this.setState({ paddleOrientation: PaddleOrientation.Left });
    }
  };

  private handleChildOverflowLeftToRight = (overflowElement: Element) => {
    // Ribbon paddles will be add if there is content (scrollWidth) outside of what can be seen (clientWidth).
    // If we have the scrollWidth is greater than the clientWidth, but the scrollLeft is 0,
    // than we just need to add a paddle to the right.
    // If we have some amount of content off the screen to the left, but we the width is
    // still greater than the clientWidth we need to add a paddle on both sides.
    // Else we add it to just the left
    // If the scrollWidth is less than or equal to the clientWidth the paddles will be hidden
    if (overflowElement.scrollLeft < this.paddlePixelThreshold) {
      // Make sure scrollLeft is 0 if only showing the Right paddle.
      overflowElement.scrollLeft = 0;

      this.setState({ paddleOrientation: PaddleOrientation.Right });
    } else if (
      overflowElement.scrollWidth - overflowElement.scrollLeft >
      overflowElement.clientWidth
    ) {
      // If the content that remains off screener is less than the size of the paddle, scroll
      // it into view.
      if (
        overflowElement.scrollWidth -
          overflowElement.scrollLeft -
          overflowElement.clientWidth <=
        appPaddleWidth
      ) {
        overflowElement.scrollLeft =
          overflowElement.scrollLeft + appPaddleWidth;
        this.setState({ paddleOrientation: PaddleOrientation.Left });
        return;
      }

      this.setState({ paddleOrientation: PaddleOrientation.LeftRight });
    } else {
      this.setState({ paddleOrientation: PaddleOrientation.Left });
    }
  };

  private getPaddleOriginationBasedOnRTL() {
    if (this.isRTL) {
      switch (this.state.paddleOrientation) {
        case PaddleOrientation.Right:
          return PaddleOrientation.Left;
        case PaddleOrientation.Left:
          return PaddleOrientation.Right;
        case PaddleOrientation.LeftRight:
          return PaddleOrientation.LeftRight;
        default:
          return PaddleOrientation.Hidden;
      }
    }

    return this.state.paddleOrientation;
  }

  /**
   * Function to handle execute of ribbon paddles.
   */
  private onPaddleButtonExecute = (id: string) => {
    this.props.onPaddleExecuted?.(id);

    if (this.container.current && this.container.current.children) {
      const overflowElement = this.container.current.children[0];

      const paddleOrientation = this.getPaddleOriginationBasedOnRTL();
      const unseenContentWidthSinglePaddle =
        overflowElement.scrollWidth - overflowElement.clientWidth;
      const unseenContentWidthDoublePaddle =
        unseenContentWidthSinglePaddle - overflowElement.scrollLeft;

      if (paddleOrientation === PaddleOrientation.LeftRight) {
        // If the current state is that both paddles are displayed and the right paddle is executed.
        // If the width of the content that isn't seen by the user is less than the clientWidth, we can
        // scroll to the end and show just the left paddle
        // Otherwise we need to scroll the ribbon to the right the width of the view area and keep
        // both paddles
        // This is the same but in reverse if the LeftPaddle is being executed
        if (unseenContentWidthDoublePaddle === 0) {
          this.setState({ paddleOrientation: PaddleOrientation.Hidden });
          return;
        }

        if (id === "RightPaddle") {
          if (unseenContentWidthDoublePaddle <= overflowElement.clientWidth) {
            overflowElement.scrollLeft = this.isRTL
              ? overflowElement.scrollLeft - unseenContentWidthDoublePaddle
              : overflowElement.scrollLeft + unseenContentWidthDoublePaddle;
            this.setState({ paddleOrientation: PaddleOrientation.Left });
          } else {
            overflowElement.scrollLeft =
              overflowElement.scrollLeft + overflowElement.clientWidth;
          }
        } else {
          if (
            unseenContentWidthDoublePaddle <=
            overflowElement.clientWidth + this.paddlePixelThreshold
          ) {
            overflowElement.scrollLeft = this.isRTL
              ? overflowElement.scrollLeft + unseenContentWidthDoublePaddle
              : 0;
            this.setState({ paddleOrientation: PaddleOrientation.Right });
          } else {
            overflowElement.scrollLeft =
              overflowElement.scrollLeft - overflowElement.clientWidth;
          }
        }
      } else if (paddleOrientation === PaddleOrientation.Right) {
        // If the right paddle has been executed, and the content that is off screen is less
        // than the viewable width then we can scroll the width of the content unseen and show just
        // the left paddle.  If the content is greater, then we are in a middle state and need to
        // show both paddles.
        if (unseenContentWidthSinglePaddle === 0) {
          this.setState({ paddleOrientation: PaddleOrientation.Hidden });
          return;
        }

        if (unseenContentWidthSinglePaddle <= overflowElement.clientWidth) {
          overflowElement.scrollLeft = unseenContentWidthSinglePaddle;
          if (this.isRTL) {
            this.setState({ paddleOrientation: PaddleOrientation.Right });
          } else this.setState({ paddleOrientation: PaddleOrientation.Left });
        } else {
          overflowElement.scrollLeft = overflowElement.clientWidth;
          this.setState({ paddleOrientation: PaddleOrientation.LeftRight });
        }
      } else {
        // Else if the left paddle was executed follow similar logic to the right.
        if (unseenContentWidthSinglePaddle === 0) {
          this.setState({ paddleOrientation: PaddleOrientation.Hidden });
          return;
        }

        if (unseenContentWidthSinglePaddle <= overflowElement.clientWidth) {
          overflowElement.scrollLeft = 0;
          if (this.isRTL) {
            this.setState({ paddleOrientation: PaddleOrientation.Left });
          } else this.setState({ paddleOrientation: PaddleOrientation.Right });
        } else {
          overflowElement.scrollLeft =
            overflowElement.scrollLeft - overflowElement.clientWidth;
          this.setState({ paddleOrientation: PaddleOrientation.LeftRight });
        }
      }
    }
  };
}
